import { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import StatusBanner from './StatusBanner';
import FileQueuePanel from './FileQueuePanel';
import ResultsPanel from './ResultsPanel';
import SettingsPanel from './SettingsPanel';
import { useBatchConvert } from '../hooks/useBatchConvert';
import * as FiIcons from 'react-icons/fi';
import type { FileSetter, TargetMimeType } from '../types';

const { FiSettings, FiZap, FiLoader, FiRefreshCw, FiChevronDown } = FiIcons;

const FORMAT_OPTIONS: Array<{ value: TargetMimeType; label: string }> = [
  { value: 'image/jpeg', label: 'JPG' },
  { value: 'image/png', label: 'PNG' },
  { value: 'image/webp', label: 'WEBP' },
];

interface ConverterDashboardProps {
  files: File[];
  setFiles: FileSetter;
}

const ConverterDashboard = ({ files, setFiles }: ConverterDashboardProps) => {
  const {
    targetFormat, setTargetFormat,
    quality, setQuality,
    resizePercent, setResizePercent,
    isConverting, progress,
    status, clearStatus,
    converted, clearConverted,
    handleConvert, downloadAll, downloadSingle,
  } = useBatchConvert(files);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);

  const handleReset = () => { setFiles([]); clearConverted(); clearStatus(); };

  const hasCustomSettings = quality !== 90 || resizePercent !== 100;

  if (!files.length && !converted.length) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      aria-label="Converter workspace"
      className="w-full max-w-5xl mx-auto mb-20 space-y-6"
    >
      <AnimatePresence>
        {status.message && <StatusBanner status={status} onDismiss={clearStatus} />}
      </AnimatePresence>

      {/* Toolbar */}
      <div className={`glass relative rounded-3xl p-5 md:p-6 shadow-xl flex flex-col md:flex-row gap-4 items-start md:items-center justify-between ${settingsOpen ? 'z-[70]' : 'z-10'}`}>
        <div>
          <h2 className="text-lg font-extrabold text-slate-800 dark:text-white">
            {converted.length
              ? `${converted.length} file${converted.length > 1 ? 's' : ''} ready to download`
              : `${files.length} file${files.length > 1 ? 's' : ''} queued`}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">All processing is 100% local. Your files never leave your device.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {!converted.length && (
            <>
              {/* Format selector */}
              <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700">
                <SafeIcon icon={FiSettings} className="text-slate-400" />
                <select
                  value={targetFormat}
                  onChange={(e) => setTargetFormat(e.target.value as TargetMimeType)}
                  disabled={isConverting}
                  aria-label="Output format"
                  className="bg-white dark:bg-slate-800 font-bold text-slate-700 dark:text-slate-200 outline-none cursor-pointer text-sm focus-visible:ring-2 focus-visible:ring-indigo-500 rounded"
                >
                  {FORMAT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Settings button */}
              <div ref={settingsRef} className="relative z-[80]">
                <button
                  type="button"
                  onClick={() => setSettingsOpen(o => !o)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-[background-color,border-color,color,box-shadow] border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                    hasCustomSettings
                      ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400'
                      : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                  aria-expanded={settingsOpen}
                  aria-label="Conversion settings"
                >
                  <SafeIcon icon={FiSettings} />
                  <span>Settings</span>
                  {hasCustomSettings && (
                    <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                  )}
                  <SafeIcon icon={FiChevronDown} className={`text-xs transition-transform ${settingsOpen ? 'rotate-180' : ''}`} />
                </button>
                <SettingsPanel
                  isOpen={settingsOpen}
                  onClose={() => setSettingsOpen(false)}
                  quality={quality}
                  setQuality={setQuality}
                  resizePercent={resizePercent}
                  setResizePercent={setResizePercent}
                />
              </div>
            </>
          )}

          {converted.length > 0 ? (
            <button
              type="button"
              onClick={handleReset}
              className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-5 py-2.5 rounded-xl font-bold text-sm transition-[background-color,border-color,color,transform] border border-slate-200 dark:border-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <SafeIcon icon={FiRefreshCw} />
              Convert More
            </button>
          ) : (
            <button
              type="button"
              onClick={handleConvert}
              disabled={isConverting || !files.length}
              aria-busy={isConverting}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 active:scale-95 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition-[background-color,box-shadow,transform,opacity] disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/20 min-w-[170px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950"
            >
              {isConverting ? (
                <>
                  <SafeIcon icon={FiLoader} className="animate-spin" />
                  {progress}% Converting…
                </>
              ) : (
                <>
              <SafeIcon icon={FiZap} />
                  Convert {files.length} Image{files.length > 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <AnimatePresence>
        {isConverting && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden"
          >
            <motion.div
              className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ ease: 'easeOut' }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {converted.length > 0 ? (
        <ResultsPanel converted={converted} onDownloadSingle={downloadSingle} onDownloadAll={downloadAll} />
      ) : (
        <div className="relative z-0">
          <FileQueuePanel files={files} setFiles={setFiles} isConverting={isConverting} />
        </div>
      )}
    </motion.section>
  );
};

export default ConverterDashboard;
